import { useCallback, useEffect, useState } from "react";
import type { Opportunity } from "@/types/opportunity";
import { updateOpportunitySchedulingNotes } from "@/lib/opportunity/opportunityClient";
import {
  isOpportunitySchedulingNotesEditable,
  SCHEDULING_NOTES_MAX_LEN,
} from "@/lib/opportunity/schedulingNotes";

const textareaClass =
  "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 min-h-[88px]";

export function OpportunitySchedulingNotesSection(props: {
  opportunity: Opportunity;
  onSaved: () => void;
}) {
  const { opportunity, onSaved } = props;
  const editable = isOpportunitySchedulingNotesEditable(opportunity.status);
  const [draft, setDraft] = useState(opportunity.scheduling_notes ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedTick, setSavedTick] = useState(0);

  useEffect(() => {
    setDraft(opportunity.scheduling_notes ?? "");
  }, [opportunity.id, opportunity.scheduling_notes]);

  const save = useCallback(async () => {
    if (!editable) return;
    setBusy(true);
    setError(null);
    const res = await updateOpportunitySchedulingNotes({
      opportunity_id: opportunity.id,
      scheduling_notes: draft,
    });
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setSavedTick((n) => n + 1);
    onSaved();
  }, [draft, editable, onSaved, opportunity.id]);

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Scheduling notes</h4>
      <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
        Optional constraints for dispatch (access, timing, site contact). Copied to the publisher&apos;s
        inbound request when this opportunity closes on an inherited service offering.
      </p>
      <textarea
        className={`${textareaClass} mt-2`}
        value={draft}
        onChange={(e) => setDraft(e.target.value.slice(0, SCHEDULING_NOTES_MAX_LEN))}
        disabled={!editable || busy}
        placeholder="e.g. Gate code 4521; spray after 9am; call customer day before."
      />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {editable ? (
          <button
            type="button"
            className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
            onClick={() => void save()}
            disabled={busy || draft === (opportunity.scheduling_notes ?? "")}
          >
            {busy ? "Saving…" : "Save scheduling notes"}
          </button>
        ) : (
          <span className="text-xs text-gray-500 dark:text-zinc-400">
            Locked — opportunity is schedule-ready or lost.
          </span>
        )}
        {savedTick > 0 && !error ? (
          <span className="text-xs text-emerald-600 dark:text-emerald-400">Saved</span>
        ) : null}
      </div>
      {error ? (
        <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100">
          {error}
        </div>
      ) : null}
    </section>
  );
}
